import React, { type FC, useState } from 'react';
import { Button, Space, Title } from '@g2u/ui-kit';
import { Dropdown, Table } from 'antd';

import { Customer, CustomerProduct, PrepaidProductStatus, PrepaidProductStatusLabels } from 'types';
import { CustomerSearchModal } from 'components';
import styles from './customer-prepaid-products-list.module.scss';

export namespace CustomerPrepaidProductsList {
  type Props = {
    isLoading?: boolean;
    products?: CustomerProduct[];
    onTransfer: (productId: number, customerId: number, name: string) => void;
    onStatusUpdate: (customerProductId: number, status: PrepaidProductStatus) => void;
  };

  export const $: FC<Props> = (props) => {
    const { products, onTransfer, isLoading, onStatusUpdate } = props;
    const [selectedProduct, setSelectedProduct] = useState<number>();
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

    const items = [
      {
        label: 'Mark as cancelled',
        key: PrepaidProductStatus.Cancelled,
      },
      {
        label: 'Mark as refunded',
        key: PrepaidProductStatus.Refunded,
      },
    ];

    const handleCustomerProductStatus = (customerProductId: number, status: PrepaidProductStatus) => {
      onStatusUpdate(customerProductId, status);
    };

    const handleCustomerSelect = (productId: number) => {
      setSelectedProduct(productId);
      setIsCustomerModalOpen(true);
    };

    const handleTransfer = (customerId: number, name: string) => {
      if (selectedProduct) {
        onTransfer(selectedProduct, customerId, name);
        setIsCustomerModalOpen(false);
      }
    };

    const columnsDefinition = [
      {
        key: 'name',
        title: 'Name',
        dataIndex: 'product_name',
      },
      {
        key: 'date',
        title: 'Purchase Date',
        dataIndex: 'purchase_date',
      },
      {
        key: 'status',
        title: 'Status',
        render: (record: CustomerProduct) => (
          <span className={styles.customePrepaidProductsList__status}>{PrepaidProductStatusLabels[record.status]}</span>
        ),
      },
      {
        key: 'actions',
        title: 'Actions',
        render: (_: any, record: CustomerProduct) => (
          <>
            <Dropdown
              menu={{
                items,
                onClick: (selectedItem) =>
                  handleCustomerProductStatus(record.ID, selectedItem.key as PrepaidProductStatus),
              }}
              arrow={{ pointAtCenter: true }}
              disabled={record.status !== PrepaidProductStatus.New}
              placement={'topRight'}
            >
              <Button.$ type={Button.Type.Link} style={{ padding: 0 }} onClick={() => null} dataTestId="edit-btn">
                Edit
              </Button.$>
            </Dropdown>
            <br />
            <Button.$
              dataTestId="transfer-product"
              type={Button.Type.Link}
              style={{ padding: 0 }}
              disabled={record.status !== PrepaidProductStatus.New}
              onClick={() => handleCustomerSelect(record.ID)}
            >
              Transfer
            </Button.$>
          </>
        ),
      },
    ];

    return (
      <div className={styles.customePrepaidProductsList}>
        <Space.$ direction={Space.Direction.Horizontal} align={Space.Align.Center} justify={Space.Justify.SpaceBetween}>
          <Title.$ level={5} className={styles.customePrepaidProductsList__title}>
            Prepaid Products
          </Title.$>
        </Space.$>
        <br />
        <Table columns={columnsDefinition} dataSource={products} loading={isLoading} pagination={{ pageSize: 5 }} />
        <CustomerSearchModal.$
          customer={null}
          isOpen={isCustomerModalOpen}
          modalTitle={'Search Customer Files'}
          actions={[
            {
              title: 'Transfer',
              action: (record: Customer) => {
                handleTransfer(record.ID, record.name.full_name);
              },
            },
          ]}
          onOk={() => setIsCustomerModalOpen(false)}
          onCancel={() => setIsCustomerModalOpen(false)}
          emptyContent={() => null}
        />
      </div>
    );
  };
}
